import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

const POLL_INTERVAL_MS = 5000;
const ORDER_DING_ENABLED_KEY = 'gng_ownerOrderDingEnabled';
const ORDER_DING_SESSION_INIT_KEY = 'gng_ownerOrderDingSessionInitialized';
const ORDER_DING_CHANGED_EVENT = 'gng-owner-ding-changed';

function playNewOrderDing(audioCtxRef) {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.setValueAtTime(1174.66, now + 0.09);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.24);
  } catch {
    // Non-fatal.
  }
}

export default function OwnerOrderAlerts() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const prefix = location.pathname.startsWith('/__test__') ? '/__test__' : '';
  const waitingOrderIdsRef = useRef(new Set());
  const hasLoadedOnceRef = useRef(false);
  const fetchOrdersRef = useRef(null);
  const toastTimerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [dingEnabled, setDingEnabled] = useState(() => {
    // Start each browser session with sound OFF by default.
    if (sessionStorage.getItem(ORDER_DING_SESSION_INIT_KEY) !== '1') {
      localStorage.setItem(ORDER_DING_ENABLED_KEY, '0');
      sessionStorage.setItem(ORDER_DING_SESSION_INIT_KEY, '1');
    }
    return localStorage.getItem(ORDER_DING_ENABLED_KEY) === '1';
  });
  const [toast, setToast] = useState(null);
  const [soundPromptOpen, setSoundPromptOpen] = useState(() => localStorage.getItem(ORDER_DING_ENABLED_KEY) !== '1');

  const fetchOrders = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return;
    const res = await api('/api/owners/me/orders', { getToken });
    if (!res.ok) return;

    const data = await res.json().catch(() => ({}));
    const list = Array.isArray(data.orders) ? data.orders : [];
    const waiting = list.filter((o) => ['pending', 'paid'].includes((o?.status || '').toLowerCase()));
    const currentIds = new Set(waiting.map((o) => o.id));

    if (hasLoadedOnceRef.current) {
      let newCount = 0;
      currentIds.forEach((id) => {
        if (!waitingOrderIdsRef.current.has(id)) newCount += 1;
      });
      if (newCount > 0) {
        if (dingEnabled) playNewOrderDing(audioCtxRef);
        setToast({
          count: newCount,
          message: newCount === 1 ? 'You have a new order' : `You have ${newCount} new orders`,
        });
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setToast(null), 6000);
      }
    }

    waitingOrderIdsRef.current = currentIds;
    hasLoadedOnceRef.current = true;
  }, [dingEnabled, getToken, isLoaded, isSignedIn]);

  fetchOrdersRef.current = fetchOrders;

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === ORDER_DING_ENABLED_KEY) {
        const enabled = e.newValue === '1';
        setDingEnabled(enabled);
        setSoundPromptOpen(!enabled);
      }
    };
    const onDingChanged = (e) => {
      const enabled = Boolean(e?.detail?.enabled);
      setDingEnabled(enabled);
      setSoundPromptOpen(!enabled);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(ORDER_DING_CHANGED_EVENT, onDingChanged);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(ORDER_DING_CHANGED_EVENT, onDingChanged);
    };
  }, []);

  useEffect(() => {
    // Browsers block audio until first user gesture; unlock context when user interacts.
    const unlockAudio = () => {
      if (!dingEnabled) return;
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
        if (audioCtxRef.current.state === 'suspended') {
          void audioCtxRef.current.resume();
        }
      } catch {
        // Non-fatal.
      }
    };
    window.addEventListener('pointerdown', unlockAudio, { passive: true });
    window.addEventListener('keydown', unlockAudio);
    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, [dingEnabled]);

  useEffect(() => {
    // If user navigates while sound is off, remind again.
    if (!dingEnabled) setSoundPromptOpen(true);
  }, [dingEnabled, location.pathname]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return () => {};
    let cancelled = false;
    (async () => {
      await fetchOrders();
      if (cancelled) return;
    })();
    const interval = setInterval(() => fetchOrdersRef.current?.(), POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchOrdersRef.current?.();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchOrders, isLoaded, isSignedIn]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (audioCtxRef.current) {
        void audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    };
  }, []);

  const enableSound = () => {
    localStorage.setItem(ORDER_DING_ENABLED_KEY, '1');
    setDingEnabled(true);
    window.dispatchEvent(new CustomEvent(ORDER_DING_CHANGED_EVENT, { detail: { enabled: true } }));
    playNewOrderDing(audioCtxRef);
    setSoundPromptOpen(false);
  };

  const dismissSoundPrompt = () => {
    setSoundPromptOpen(false);
  };

  return (
    <>
      {soundPromptOpen && (
        <div className="fixed right-4 left-4 sm:left-auto sm:max-w-sm bottom-[calc(6rem+env(safe-area-inset-bottom))] z-[96]">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg p-3">
            <div className="flex items-start gap-3">
              <div className="text-xl leading-none">🔔</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Turn on order sound?</p>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">Get an instant alert for new orders.</p>
              </div>
            </div>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={enableSound}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
              >
                Turn on
              </button>
              <button
                type="button"
                onClick={dismissSoundPrompt}
                className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-sm"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed right-4 left-4 sm:left-auto sm:max-w-sm bottom-[calc(6rem+env(safe-area-inset-bottom))] z-[95]">
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-700 bg-white dark:bg-slate-800 shadow-lg p-3">
            <div className="flex items-start gap-3">
              <div className="text-xl leading-none">🔔</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{toast.message}</p>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                  Tap to open Order Queue.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setToast(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                aria-label="Dismiss order alert"
              >
                ×
              </button>
            </div>
            <Link
              to={`${prefix}/owner/orders`}
              onClick={() => setToast(null)}
              className="mt-2 inline-block text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:underline"
            >
              View orders
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

